import { z } from "zod";

import { clubCountryCodes } from "./countries";
import { clubHosting, clubTraveling, deDivisionsList, frDivisionsList, itDivisionsList, playerFFTranking, playerGenders, playerPreferredSurface, rankingTypes } from "./enums";
import { ATP_WTA_ITF_Regex, UTRRegex } from "./player";
import { phoneNumberSchema } from "./user";
import { ATP_WTA_ITF_ErrorMsg, countryMsg, descriptionMsg, emailMsg, minLengthMsg, priceMsg, requiredMsg, UTR_ErrorMsg } from "./zod-error-msg";

export const clubInformationsSchema = z.object({
  clubRepresentativeFirstName: z.string().min(2, { message: minLengthMsg }),
  clubRepresentativeLastName: z.string().min(2, { message: minLengthMsg }),
  clubName: z.string().min(2, { message: minLengthMsg }),
  clubAddress: z.string().min(2, { message: minLengthMsg }),
  clubCity: z.string().min(2, { message: minLengthMsg }),
  clubCountryCode: z.enum(clubCountryCodes, { message: countryMsg }),
  clubRepresentativeEmail: z.string().email({ message: emailMsg }),
  clubRepresentativePhone: phoneNumberSchema,
  clubEmail: z.string().email({ message: emailMsg }),
  clubPhone: phoneNumberSchema,
  imgUrl: z.string().url().optional(),
  clubDescription: z.string().max(750, { message: descriptionMsg }).optional(),
});
export type ClubInformationsSchema = z.infer<typeof clubInformationsSchema>;

export const clubInformationsUpdateSchema = clubInformationsSchema.and(z.object({ clubRepresentativeId: z.string().uuid() }));
export type ClubInformationsUpdateSchema = z.infer<typeof clubInformationsUpdateSchema>;

export const onboardingClubSchema = clubInformationsSchema.omit({
  imgUrl: true,
  clubDescription: true,
});

export const clubSurfaceSchema = z.object({ clubSurface: z.enum(playerPreferredSurface, { message: requiredMsg }) });
export type ClubSurfaceSchema = z.infer<typeof clubSurfaceSchema>;

export const clubHostingSchema = z.object({ clubHosting: z.enum(clubHosting, { message: requiredMsg }) });
export type ClubHostingSchema = z.infer<typeof clubHostingSchema>;

export const clubTravelingSchema = z.object({ clubTraveling: z.enum(clubTraveling, { message: requiredMsg }) });
export type ClubTravelingSchema = z.infer<typeof clubTravelingSchema>;

export const clubSpecsSchema = clubSurfaceSchema.merge(clubHostingSchema).merge(clubTravelingSchema).partial();
export type ClubSpecsSchema = z.infer<typeof clubSpecsSchema>;

const divisionUnionSchema = z.union([
  z.enum(frDivisionsList),
  z.enum(deDivisionsList),
  z.enum(itDivisionsList),
  z.string(),
]);

export const clubTeamsDivisionSchema = z.array(divisionUnionSchema);
export type ClubTeamsDivisionSchema = z.infer<typeof clubTeamsDivisionSchema>;

export const clubTeamsDivisionSchemaClient = z.object({
  firstDivision: divisionUnionSchema.optional(),
  secondDivision: divisionUnionSchema.optional(),
  thirdDivision: divisionUnionSchema.optional(),
});
export type ClubTeamsDivisionSchemaClient = z.infer<typeof clubTeamsDivisionSchemaClient>;

export const clubTeamsDivisionSchemaServer = z.object({
  clubTeamsMenDivision: clubTeamsDivisionSchema,
  clubTeamsWomenDivision: clubTeamsDivisionSchema,
});

export const matchOfferSchema = z.object({
  id: z.string().optional(),
  playerGender: z.enum(playerGenders, { message: requiredMsg }),
  playerRankingType: z.enum(rankingTypes, { message: requiredMsg }),
  playerRanking: z.string({ message: requiredMsg }),
  playerRankingNumber: z.coerce.number({ message: requiredMsg }).positive({ message: requiredMsg }).optional(),
  matchPrice: z.literal("").transform(() => null).or(z.coerce.number().positive({ message: priceMsg })).nullable(),
}).superRefine((data, ctx) => {
  if (data.playerRanking === "T100" && data.playerRankingNumber === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["rankingNumber"],
      message: requiredMsg,
    });
  }
  if (data.playerRankingType === "FFT") {
    if (!playerFFTranking.includes(data.playerRanking as any)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ranking"],
        message: requiredMsg,
      });
    }
  }
  else if (data.playerRankingType === "UTR") {
    if (!UTRRegex.test(data.playerRanking)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ranking"],
        message: UTR_ErrorMsg,
      });
    }
  }
  else if (["ATP", "WTA", "ITF"].includes(data.playerRankingType)) {
    if (!ATP_WTA_ITF_Regex.test(data.playerRanking)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ranking"],
        message: ATP_WTA_ITF_ErrorMsg,
      });
    }
  }
});
export type MatchOfferSchema = z.infer<typeof matchOfferSchema>;

export const matchOfferSchemaDelete = z.object({
  id: z.string(),
});

export const updateClubFavoritesSchema = z.object({ playerId: z.string().uuid() });
export type UpdateClubFavoritesSchema = z.infer<typeof updateClubFavoritesSchema>;

export const paymentStatusValues = ["pending", "completed", "failed", "refunded"] as const;
export type PaymentStatus = (typeof paymentStatusValues)[number];
export const paymentStatusEnum = z.enum(paymentStatusValues);

// "premium" est le plan unique de la nouvelle grille (275 €/an, arbitrage client
// du 13/07/2026). Les trois pack_* restent déclarés : des clubs y sont abonnés en
// production et leur abonnement Stripe n'est pas migré. Ils ne sont simplement
// plus proposés à la vente (voir sellableClubPlans).
export const clubPlan = ["pack_equipe", "pack_tournoi", "pack_equipe_tournoi", "premium"] as const;
export type ClubPlanType = (typeof clubPlan)[number];
export const clubPlanEnum = z.enum(clubPlan);

// Plans encore achetables depuis la page /plans. Les pack_* sont en extinction :
// on honore les abonnements en cours, on n'en crée plus.
export const sellableClubPlans = ["premium"] as const satisfies readonly ClubPlanType[];
export type SellableClubPlanType = (typeof sellableClubPlans)[number];
export const sellableClubPlanEnum = z.enum(sellableClubPlans);

export const planDico: Record<ClubPlanType, string> = {
  pack_equipe: "common:plan.pack_equipe",
  pack_tournoi: "common:plan.pack_tournoi",
  pack_equipe_tournoi: "common:plan.pack_equipe_tournoi",
  premium: "common:plan.premium",
};
export type PlanDico = typeof planDico;
